import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { WorkspaceListItem } from '@/lib/api/features/authentication/types/PostGetCurrentUserResInterface'
import { Button } from '@/components/ui/button'
import useLoginStore from '@/store/LoginStore'
import getWorkspaces from '@/lib/api/features/authentication/api/getWorkspaces'
import { MolyLogoFull } from '@/components/app/MolyLogoFull'

export const Route = createFileRoute('/_AppWrapper/workspaces')({
  component: WorkspaceSelectionPage,
})

function WorkspaceAvatar({ workspace }: { workspace: WorkspaceListItem }) {
  if (workspace.logoUrl) {
    return (
      <div className="flex size-10 items-center justify-center overflow-hidden rounded-lg flex-shrink-0">
        <img
          src={workspace.logoUrl}
          alt={workspace.name}
          className="h-full w-full object-cover"
        />
      </div>
    )
  }

  return (
    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary text-sm font-semibold flex-shrink-0">
      {workspace.name.charAt(0).toUpperCase()}
    </div>
  )
}

function WorkspaceSelectionPage() {
  const navigate = useNavigate()
  const workspaces = useLoginStore((s) => s.workspaces)
  const switchWorkspace = useLoginStore((s) => s.switchWorkspace)
  const username = useLoginStore((s) => s.username)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingWorkspaceId, setLoadingWorkspaceId] = useState<string | null>(null)

  // Refresh workspace list on mount
  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const resp = await getWorkspaces()
        useLoginStore.getState().setWorkspaces(resp.data)
      } catch {
        // If fetch fails, use cached workspaces from store
      }
    }
    void fetchWorkspaces()
  }, [])

  const handleOpen = async (workspace: WorkspaceListItem) => {
    setLoadingWorkspaceId(workspace.workspaceId)
    setIsLoading(true)
    switchWorkspace(workspace)
    await navigate({ to: `/${workspace.workspaceSlug}` })
  }

  const handleChangeEmail = () => {
    useLoginStore.getState().reset()
    window.location.href = '/auth'
  }

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center bg-background p-6 md:p-10">
      {/* Logo — top-left */}
      <div className="absolute left-6 top-6 md:left-10 md:top-10">
        <MolyLogoFull className="h-12" />
      </div>

      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-8">
          <h1 className="text-2xl font-semibold tracking-tight">
            Select a workspace
          </h1>

          <div className="w-full space-y-2">
            {workspaces.map((ws) => (
              <div
                key={ws.workspaceId}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <WorkspaceAvatar workspace={ws} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium truncate">
                      {ws.name}
                    </span>
                    {ws.description && (
                      <span className="text-xs text-muted-foreground truncate">
                        {ws.description}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void handleOpen(ws)}
                  disabled={isLoading}
                >
                  {loadingWorkspaceId === ws.workspaceId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Open'
                  )}
                </Button>
              </div>
            ))}
          </div>

          {workspaces.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No workspaces found. You may need to be invited to one.
            </p>
          )}

          {/* Change email section */}
          <div className="w-full pt-8 border-t">
            <p className="text-sm text-muted-foreground mb-3">
              Don't see your workspace? Try a different email:
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{username}</span>
              <Button variant="outline" size="sm" onClick={handleChangeEmail}>
                Change
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
